import { AxiosError, AxiosResponse } from 'axios';
import { Dispatch } from '@reduxjs/toolkit';

import { GlobalService } from './global.service';

import { AXIOS, API, CommonUserService, UserUmanforge, Users, UserFilter, PostUser, UserOfSkillLinked, UserOfAbilityLinked, CommonGlobalService, UUID, CallbackErrorAxios, CallbackWarningAxios, Client } from '@umanforge/common';
import { ModifyUser } from 'interfaces/api_user.interfaces';
import { ModifyHolidayLocation } from 'interfaces/api_holiday.interfaces';
import { PostMissingDayToUser } from 'interfaces/api_missing_day.interfaces';
import { HmacSHA1 } from 'crypto-js';
import { ImportError } from 'interfaces/api_import.interfaces';
import { isHeaderProblem } from 'utils/import.utils';

/* ===== Interfaces ===== */
interface FlashmeetingAction {
  id: string;
  eventId?: string;
  eventManagerId?: string;
  clientId?: string;
  siteId?: string;
  sectorId?: string;
  title: string;
  author: string;
  createdDate: string;
  engagementDate: string;
  theme: {
    name: string;
    color: string;
  };
  daysLate: number;
  isRecurrent: boolean;
  isShared: boolean;
  isCommon: boolean;
}

interface TaskActionData {
  id: string;
  name: string;
  icon: string;
}

interface LocationData {
  name: string | null;
}

interface PlaniteamAction {
  id: string;
  idPlanning: string;
  location: LocationData | null;
  taskAction: TaskActionData | null;
  status: string;
  dateStart: string;
  dateEnd: string;
  debord: number;
  nbSmallPackage: number;
  nbBigPackage: number;
  recurring: number;
}


export class UserService {

  static getUser(
    userId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<UserUmanforge> {
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, dispatch, onError ); };
    return CommonUserService.getUser( userId, token, dispatch, onAxiosWarning, onAxiosError );
  }

  static getAllowedUsers(
    filters: UserFilter,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Users> {
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, dispatch, onError ); };
    return CommonUserService.getAllowedUsers( filters, token, dispatch, onAxiosWarning, onAxiosError );
  }

  static getUsersOfAbility(
    abilityId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<UserOfAbilityLinked[]> {
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, dispatch, onError ); };
    return CommonUserService.getUsersOfAbility( abilityId, token, dispatch, onAxiosWarning, onAxiosError );
  }

  static getUsersOfSkill(
    skillId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<UserOfSkillLinked[]> {
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, dispatch, onError ); };
    return CommonUserService.getUsersOfSkill( skillId, token, dispatch, onAxiosWarning, onAxiosError );
  }

  static getUsersOfClient(
    clientId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<UserUmanforge[]> {
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, dispatch, onError ); };
    return CommonUserService.getUsersOfClient( clientId, token, dispatch, onAxiosWarning, onAxiosError );
  }

  static getAdminsOfClient(
    clientId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<UserUmanforge[]> {
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, dispatch, onError ); };
    return CommonUserService.getAdminsOfClient( clientId, token, dispatch, onAxiosWarning, onAxiosError );
  }

  static getUsersOfSites(
    sitesId: UUID[],
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<UserUmanforge[]> {
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, dispatch, onError ); };
    return CommonUserService.getUsersOfSites( sitesId, token, dispatch, onAxiosWarning, onAxiosError );
  }

  static getAdminsOfSite(
    siteId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<UserUmanforge[]> {
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, dispatch, onError ); };
    return CommonUserService.getAdminsOfSite( siteId, token, dispatch, onAxiosWarning, onAxiosError );
  }

  static getUsersOfSector(
    sectorId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<UserUmanforge[]> {
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, dispatch, onError ); };
    return CommonUserService.getUsersOfSector( sectorId, token, dispatch, onAxiosWarning, onAxiosError );
  }

  static getAdminsOfSector(
    sectorId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<UserUmanforge[]> {
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, dispatch, onError ); };
    return CommonUserService.getAdminsOfSector( sectorId, token, dispatch, onAxiosWarning, onAxiosError );
  }

  static postUser(
    body: PostUser,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<string | null> {
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, dispatch, onError ); };
    return CommonUserService.postUser( body, token, dispatch, onAxiosWarning, onAxiosError );
  }

  static deleteUser(
    userId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.delete( API.USERS + userId, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  static putUser(
    userId: UUID, body: ModifyUser,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.put( API.USERS + userId, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  static putPasswordUser(
    userId: UUID, password: string, seed: string,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      // eslint-disable-next-line new-cap
      AXIOS.put( API.USERS + userId + '/password', { password: HmacSHA1( password, seed ).toString() }, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  static uploadAvatar(
    userId: UUID, clientId: UUID, file: File,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<string | null> {
    const onAxiosWarning = ( response : AxiosResponse ) => { GlobalService.onAxiosWarning( response, onWarning ); };
    const onAxiosError = ( err : AxiosError ) => { GlobalService.onAxiosError( err, dispatch, onError ); };
    return new Promise( async resolve => {
      try {
        const formData = new FormData();
        formData.append( 'content', file );
        const baseConfig = await CommonGlobalService.getHeaderRequest( token );
        const config = { ...baseConfig, headers: { ...( ( baseConfig && ( baseConfig ).headers ) || {} ), 'Content-Type': 'multipart/form-data' } };
        const response = await AXIOS.post( API.USERS + `${userId}/avatar/${clientId}`, formData, config );
        CommonGlobalService.saveRequestToken( response, dispatch );
        if ( response.status === 200 ) {
          resolve( response.data && response.data.avatar ? response.data.avatar as string : null );
          return;
        }
        onAxiosWarning( response );
        resolve( null );
      } catch ( err ) {
        onAxiosError( err as AxiosError );
        resolve( null );
      }
    } );
  }

  static resetPasswordUser(
    userId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.put( API.USERS + userId + '/resetPassword', {}, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  static unlockUser(
    userId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.put( API.USERS + userId + '/unlock', {}, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  static disableUser(
    userId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.put( API.USERS + userId + '/disable', {}, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  static enableUser(
    userId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.put( API.USERS + userId + '/enable', {}, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  static deleteUserToClients(
    userId: UUID, body: { clients: UUID[] },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.delete( API.USERS + userId + '/clients/user', { ...await CommonGlobalService.getHeaderRequest( token ), data: body } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static postAdminToClients(
    userId: UUID, body: { clients: UUID[] },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.post( API.USERS + userId + '/clients/admin', body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static deleteAdminToClients(
    userId: UUID, body: { clients: UUID[] },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.delete( API.USERS + userId + '/clients/admin', { ...await CommonGlobalService.getHeaderRequest( token ), data: body } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static postAdminToSites(
    userId: UUID, body: { sites: UUID[] },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.post( API.USERS + userId + '/sites/admin', body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static deleteAdminToSites(
    userId: UUID, body: { sites: UUID[] },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.delete( API.USERS + userId + '/sites/admin', { ...await CommonGlobalService.getHeaderRequest( token ), data: body } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static postUserToSectors(
    userId: UUID, body: { sectors: UUID[] },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.post( API.USERS + userId + '/sectors/user', body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static deleteUserToSectors(
    userId: UUID, body: { sectors: UUID[] },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.delete( API.USERS + userId + '/sectors/user', { ...await CommonGlobalService.getHeaderRequest( token ), data: body } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static postAdminToSectors(
    userId: UUID, body: { sectors: UUID[] },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.post( API.USERS + userId + '/sectors/admin', body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static deleteAdminToSectors(
    userId: UUID, body: { sectors: UUID[] },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.delete( API.USERS + userId + '/sectors/admin', { ...await CommonGlobalService.getHeaderRequest( token ), data: body } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static postUserToAbilities(
    userId: UUID, body: { abilities: { id: UUID, expirationDate: string }[] },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.post( API.USERS + userId + '/abilities', body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static deleteUserToAbilities(
    userId: UUID, body: { abilities: UUID[] },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.delete( API.USERS + userId + '/abilities', { ...await CommonGlobalService.getHeaderRequest( token ), data: body } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static postUserToSkills(
    userId: UUID, body: { skills: { id: UUID, value: number }[] },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.post( API.USERS + userId + '/skills', body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static deleteUserToSkills(
    userId: UUID, body: { skills: UUID[] },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.delete( API.USERS + userId + '/skills', { ...await CommonGlobalService.getHeaderRequest( token ), data: body } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  static deleteHolidayLocationOfUser(
    userId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.delete( API.USERS + userId + '/holidays_location', await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          if ( onError ) {
            onError( err );
          }
          GlobalService.onAxiosError( err, dispatch );
          resolve( false );
        } );
    } );
  }

  static putHolidayLocationOfUser(
    userId: UUID, body: ModifyHolidayLocation,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.put( API.USERS + userId + '/holidays_location', body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          if ( onError ) {
            onError( err );
          }
          GlobalService.onAxiosError( err, dispatch );
          resolve( false );
        } );
    } );
  }

  static postExcludedHolidaysToUser(
    userId: UUID, rules: string[],
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.post( API.USERS + userId + '/holidays', { rules }, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          if ( onError ) {
            onError( err );
          }
          GlobalService.onAxiosError( err, dispatch );
          resolve( false );
        } );
    } );
  }

  static removedExcludedHolidaysToUser(
    userId: UUID, rules: string[],
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.delete( API.USERS + userId + '/holidays', { ...await CommonGlobalService.getHeaderRequest( token ), params: { rules } } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          if ( onError ) {
            onError( err );
          }
          GlobalService.onAxiosError( err, dispatch );
          resolve( false );
        } );
    } );
  }

  static postMissingDaysOfUser(
    userId: UUID, newMissingDays: PostMissingDayToUser[],
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.post( API.USERS + userId + '/missing_days', { missingDays: newMissingDays }, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          if ( onError ) {
            onError( err );
          }
          GlobalService.onAxiosError( err, dispatch );
          resolve( false );
        } );
    } );
  }

  static deleteMissingDaysOfUser(
    missingDaysId: UUID[],
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.delete( API.USERS + '/missing_days', { ...await CommonGlobalService.getHeaderRequest( token ), params: { missingDaysId } } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          if ( onError ) {
            onError( err );
          }
          GlobalService.onAxiosError( err, dispatch );
          resolve( false );
        } );
    } );
  }

  static getUserActionsFlashmeeting(
    userId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios
  ): Promise<FlashmeetingAction[]> {
    return new Promise( async resolve => {
      try {
        const url = `/api/fm/engagements/users/${userId}/future-engagements`;
        const response = await AXIOS.get(
          url,
          await CommonGlobalService.getHeaderRequest( token )
        );
        CommonGlobalService.saveRequestToken( response, dispatch );
        if ( response.status === 200 ) {
          resolve( response.data || [] );
        } else {
          resolve( [] );
        }
      } catch ( err ) {
        const axiosErr = err as AxiosError;
        console.error( 'getUserActionsFlashmeeting - Error:', axiosErr );
        if ( onError ) {
          onError( axiosErr );
        }
        GlobalService.onAxiosError( axiosErr, dispatch, onError );
        resolve( [] );
      }
    } );
  }

  static getUserActionsPlaniteam(
    userId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios
  ): Promise<PlaniteamAction[]> {
    return new Promise( async resolve => {
      try {
        const response = await AXIOS.get(
          `/api/fp/task_action/users/${userId}/future-actions`,
          await CommonGlobalService.getHeaderRequest( token )
        );
        CommonGlobalService.saveRequestToken( response, dispatch );
        if ( response.status === 200 ) {
          resolve( response.data || [] );
        } else {
          resolve( [] );
        }
      } catch ( err ) {
        const axiosErr = err as AxiosError;
        if ( onError ) {
          onError( axiosErr );
        }
        GlobalService.onAxiosError( axiosErr, dispatch, onError );
        resolve( [] );
      }
    } );
  }

  static exportUsersOfClient(
    client: Client,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.get( `${ API.USERS }export/${ client.id }`, { ...await CommonGlobalService.getHeaderRequest( token ), responseType: 'blob' } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }

          const blob = new Blob( [ response.data ], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          } );

          if ( response.status === 200 ) {
            const url = window.URL.createObjectURL( blob );
            const a = document.createElement( 'a' );
            a.href = url;
            a.download = `${ client.name }_users_export.xlsx`;
            document.body.appendChild( a );
            a.click();
            document.body.removeChild( a );
          }

          resolve( response.status === 200 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  static downloadUsersTemplate(
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.get( `${ API.USERS }export`, { ...await CommonGlobalService.getHeaderRequest( token ), responseType: 'blob' } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }

          const blob = new Blob( [ response.data ], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          } );

          if ( response.status === 200 ) {
            const url = window.URL.createObjectURL( blob );
            const a = document.createElement( 'a' );
            a.href = url;
            a.download = 'users_template.xlsx';
            document.body.appendChild( a );
            a.click();
            document.body.removeChild( a );
          }

          resolve( response.status === 200 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( false );
        } );
    } );
  }

  static importUsersOnClient(
    clientId: UUID, file: File, hasHeader: boolean | undefined,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<UUID[] | ImportError | null> {
    return new Promise( async resolve => {

      const formData = new FormData();
      formData.append( 'content', file );
      if ( hasHeader !== undefined ) {
        formData.append( 'hasHeader', String( hasHeader ) );
      }
      const requestConfig = await CommonGlobalService.getHeaderRequest( token );
      ( requestConfig.headers ?? {} )[ 'Content-Type' ] = 'multipart/form-data';

      AXIOS.post( `${ API.USERS }import/${ clientId }`, formData, requestConfig )
        .then( ( response: AxiosResponse ) => {
          let error: ImportError | null = null;
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 && !( isHeaderProblem( response.data ) && hasHeader === undefined ) ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          if ( response.status === 202 ) {
            error = response.data as ImportError;
          }

          resolve( response.status === 200 ? response.data.ids : error );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( null );
        } );
    } );
  }

};